import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Phone, MessageSquareText, Video, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";

const TENANT = "company-a";

export default function AdminDemoSettings() {
  const [form, setForm] = useState({ phone_number: "", sms_number: "", video_link: "", sms_prefill: "" });
  const [meta, setMeta] = useState({ updated_at: null, updated_by_name: null });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/admin/demo-settings/${TENANT}`).then((r) => {
      const { phone_number, sms_number, video_link, sms_prefill, updated_at, updated_by_name } = r.data;
      setForm({ phone_number: phone_number || "", sms_number: sms_number || "", video_link: video_link || "", sms_prefill: sms_prefill || "" });
      setMeta({ updated_at, updated_by_name });
    }).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.patch(`/admin/demo-settings/${TENANT}`, form);
      setMeta({ updated_at: r.data.updated_at, updated_by_name: r.data.updated_by_name });
      toast.success("Demo settings saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-demo-settings" className="max-w-3xl">
      <div className="flex items-center gap-2 mb-1">
        <h2 className="font-heading text-2xl tracking-tight text-slate-900">Demo Settings</h2>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[10px] uppercase tracking-[0.22em] font-semibold text-amber-800">
          <Sparkles className="w-3 h-3" strokeWidth={2.4} /> Company A
        </span>
      </div>
      <p className="text-sm text-slate-500 mb-6">Set the demo phone, SMS number, and video meeting link. Changes take effect immediately — no rebuild required.</p>

      <form onSubmit={save} className="space-y-5 bg-white border border-slate-200 rounded-2xl p-6 md:p-8">
        <Field icon={Phone} label="Demo Phone Number" hint="Native tel: link. E.g., +15551234567">
          <input type="tel" value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} data-testid="settings-phone" placeholder="+15551234567" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
        </Field>
        <Field icon={MessageSquareText} label="Demo SMS Number" hint="Leave blank to reuse the phone number.">
          <input type="tel" value={form.sms_number} onChange={(e) => setForm({ ...form, sms_number: e.target.value })} data-testid="settings-sms" placeholder="+15551234567" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
        </Field>
        <Field label="Prefilled SMS Body" hint="Optional starter text.">
          <input type="text" value={form.sms_prefill} onChange={(e) => setForm({ ...form, sms_prefill: e.target.value })} data-testid="settings-sms-prefill" placeholder="CALLACHAP Company A demonstration" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
        </Field>
        <Field icon={Video} label="Video Meeting Link" hint="Full URL. Opens in a new tab (Calendly, Google Meet, Zoom, etc.).">
          <input type="url" value={form.video_link} onChange={(e) => setForm({ ...form, video_link: e.target.value })} data-testid="settings-video" placeholder="https://meet.google.com/…" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
        </Field>
        <div className="flex items-center justify-between pt-2 flex-wrap gap-3">
          <button type="submit" disabled={saving} data-testid="settings-save" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] disabled:opacity-60 text-white text-sm font-semibold uppercase tracking-wider">
            <Save className="w-4 h-4" strokeWidth={2.2} /> {saving ? "Saving…" : "Save Settings"}
          </button>
          {meta.updated_at && <p className="text-xs text-slate-500">Last updated {new Date(meta.updated_at).toLocaleString()}{meta.updated_by_name ? ` by ${meta.updated_by_name}` : ""}</p>}
        </div>
      </form>
    </div>
  );
}

function Field({ icon: Icon, label, hint, children }) {
  return (
    <label className="block">
      <span className="flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-semibold text-slate-600 mb-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />} {label}
      </span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-slate-500">{hint}</span>}
    </label>
  );
}
